import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar, ChevronLeft, ChevronRight, Clock, FileText, AlertTriangle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SUBJECT_COLORS = {
  ICT: { bg: '#2563eb', light: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  Economics: { bg: '#059669', light: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  Mathematics: { bg: '#d97706', light: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  English: { bg: '#7c3aed', light: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
  French: { bg: '#dc2626', light: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  Physics: { bg: '#0891b2', light: '#cffafe', text: '#155e75', border: '#67e8f9' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(str) {
  const d = parseDate(str);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function daysBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function getConflicts(programs) {
  const allTasks = [];
  programs.forEach(p => {
    p.tasks.forEach(t => {
      const end = parseDate(t.final_submission_date || t.end_date);
      if (end) allTasks.push({ subject: p.subject, task: t.title, date: end });
    });
  });
  const conflicts = [];
  for (let i = 0; i < allTasks.length; i++) {
    for (let j = i + 1; j < allTasks.length; j++) {
      const diff = Math.abs(daysBetween(allTasks[i].date, allTasks[j].date));
      if (diff <= 7) {
        conflicts.push({ a: allTasks[i], b: allTasks[j], days: diff });
      }
    }
  }
  return conflicts;
}

export default function IACalendarPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAllConflicts, setShowAllConflicts] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch('/api/ia-programs/calendar', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPrograms(data.programs || []);
        }
      } catch (e) {
        console.error('Failed to load calendar:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Calculate timeline range from all task dates
  const { timelineStart, timelineEnd, totalDays, monthMarkers } = useMemo(() => {
    let earliest = new Date('2030-12-31');
    let latest = new Date('2020-01-01');

    programs.forEach(p => {
      p.tasks.forEach(t => {
        const s = parseDate(t.start_date);
        const e = parseDate(t.results_to_eau_date || t.end_date);
        if (s && s < earliest) earliest = s;
        if (e && e > latest) latest = e;
      });
    });

    // Add padding
    const start = new Date(earliest);
    start.setDate(1);
    const end = new Date(latest);
    end.setMonth(end.getMonth() + 1, 0);

    const total = daysBetween(start, end);

    // Generate month markers
    const markers = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      const offset = daysBetween(start, cursor);
      const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
      markers.push({
        label: `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`,
        offset,
        width: (daysInMonth / total) * 100,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return { timelineStart: start, timelineEnd: end, totalDays: total, monthMarkers: markers };
  }, [programs]);

  const conflicts = useMemo(() => getConflicts(programs), [programs]);

  const getPosition = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d || !timelineStart) return 0;
    return (daysBetween(timelineStart, d) / totalDays) * 100;
  };

  const getWidth = (startStr, endStr) => {
    const s = parseDate(startStr);
    const e = parseDate(endStr);
    if (!s || !e) return 2;
    return Math.max(((daysBetween(s, e)) / totalDays) * 100, 1.5);
  };

  const openTaskDetail = (task, subject) => {
    setSelectedTask({ ...task, subject });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ia-calendar-loading">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-4 md:p-6" data-testid="ia-calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="ia-calendar-title">
              <Calendar className="h-6 w-6 text-blue-600" />
              IA Timeline — Year 13 (2026)
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              All subject deadlines at a glance · {programs.length} programs · {programs.reduce((a, p) => a + p.tasks.length, 0)} tasks
            </p>
          </div>
        </div>

        {/* Conflict Warnings */}
        {conflicts.length > 0 && (
          <Card className="border-amber-300 bg-amber-50" data-testid="ia-calendar-conflicts">
            <CardContent className="py-3 px-4">
              <button
                className="flex items-center gap-2 mb-1 w-full text-left"
                onClick={() => setShowAllConflicts(!showAllConflicts)}
                data-testid="ia-calendar-conflicts-toggle"
              >
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  {conflicts.length} Scheduling Alert{conflicts.length > 1 ? 's' : ''} — tasks with deadlines within 7 days of each other
                </span>
                <ChevronLeft className={`h-4 w-4 text-amber-600 ml-auto transition-transform ${showAllConflicts ? '-rotate-90' : ''}`} />
              </button>
              {showAllConflicts && (
                <div className="space-y-1 mt-2">
                  {conflicts.map((c, i) => (
                    <p key={i} className="text-xs text-amber-700">
                      <span className="font-medium">{c.a.subject}</span> ({c.a.task.split('(')[0].trim()}) and{' '}
                      <span className="font-medium">{c.b.subject}</span> ({c.b.task.split('(')[0].trim()}) are{' '}
                      {c.days === 0 ? 'on the same day' : `only ${c.days} day${c.days > 1 ? 's' : ''} apart`}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Subject Legend */}
        <div className="flex flex-wrap gap-3" data-testid="ia-calendar-legend">
          {programs.map(p => {
            const color = SUBJECT_COLORS[p.subject] || SUBJECT_COLORS.ICT;
            return (
              <div key={p.id} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: color.bg }} />
                <span className="font-medium" style={{ color: color.text }}>
                  {p.subject} ({p.total_weighting}%)
                </span>
              </div>
            );
          })}
        </div>

        {/* Timeline Chart */}
        <Card data-testid="ia-calendar-timeline">
          <CardContent className="pt-6 pb-4 px-4 overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Month Headers */}
              <div className="flex border-b border-gray-200 mb-1">
                <div className="w-36 flex-shrink-0" />
                <div className="flex-1 relative h-8">
                  {monthMarkers.map((m, i) => (
                    <div
                      key={i}
                      className="absolute top-0 h-full flex items-center border-l border-gray-200"
                      style={{ left: `${m.offset / totalDays * 100}%`, width: `${m.width}%` }}
                    >
                      <span className="text-[11px] font-semibold text-gray-500 pl-1.5 whitespace-nowrap">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Today line reference */}
              <div className="relative">
                {/* Subject Rows */}
                {programs.map(p => {
                  const color = SUBJECT_COLORS[p.subject] || SUBJECT_COLORS.ICT;
                  return (
                    <div key={p.id} className="flex items-start border-b border-gray-100 group hover:bg-gray-50/50 transition-colors" data-testid={`ia-calendar-row-${p.subject.toLowerCase()}`}>
                      {/* Subject Label */}
                      <div className="w-36 flex-shrink-0 py-3 pr-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color.bg }} />
                          <span className="text-sm font-semibold truncate" style={{ color: color.text }}>{p.subject}</span>
                        </div>
                        <div className="text-[10px] text-gray-400 ml-[18px]">{p.tasks.length} tasks</div>
                      </div>

                      {/* Task Bars */}
                      <div className="flex-1 relative py-2" style={{ minHeight: `${p.tasks.length * 36 + 8}px` }}>
                        {/* Vertical month grid lines */}
                        {monthMarkers.map((m, i) => (
                          <div
                            key={i}
                            className="absolute top-0 bottom-0 border-l border-gray-100"
                            style={{ left: `${m.offset / totalDays * 100}%` }}
                          />
                        ))}

                        {/* Today marker */}
                        {(() => {
                          const today = new Date();
                          if (today >= timelineStart && today <= timelineEnd) {
                            const pos = getPosition(today.toISOString().split('T')[0]);
                            return (
                              <div
                                className="absolute top-0 bottom-0 w-px bg-red-400 z-10"
                                style={{ left: `${pos}%` }}
                              >
                                <div className="absolute -top-0.5 -left-1 w-2 h-2 rounded-full bg-red-500" />
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {p.tasks.map((task, tIdx) => {
                          const left = getPosition(task.start_date);
                          const width = getWidth(task.start_date, task.end_date);
                          const eauLeft = getPosition(task.results_to_eau_date);

                          return (
                            <div key={task.id} className="absolute flex items-center" style={{ left: `${left}%`, width: `${width}%`, top: `${tIdx * 36 + 4}px`, height: '28px' }}>
                              {/* Task Bar */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    className="h-full w-full rounded-md cursor-pointer transition-all hover:brightness-110 hover:shadow-md relative overflow-hidden"
                                    style={{ backgroundColor: color.bg }}
                                    onClick={() => openTaskDetail(task, p.subject)}
                                    data-testid={`ia-calendar-task-${p.subject.toLowerCase()}-${task.task_number}`}
                                  >
                                    <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-white truncate">
                                      T{task.task_number}: {task.title.split('(')[0].trim()} ({task.weighting}%)
                                    </span>
                                    {/* Draft markers inside bar */}
                                    {task.drafts && task.drafts.map((d, di) => {
                                      const dEnd = parseDate(d.end_date);
                                      const tStart = parseDate(task.start_date);
                                      const tEnd = parseDate(task.end_date);
                                      if (!dEnd || !tStart || !tEnd) return null;
                                      const taskDays = daysBetween(tStart, tEnd);
                                      if (taskDays <= 0) return null;
                                      const draftPos = (daysBetween(tStart, dEnd) / taskDays) * 100;
                                      return (
                                        <div
                                          key={di}
                                          className="absolute top-0 bottom-0 w-px bg-white/40"
                                          style={{ left: `${Math.min(draftPos, 100)}%` }}
                                        />
                                      );
                                    })}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p className="font-semibold">{task.title}</p>
                                  <p className="text-xs">Weight: {task.weighting}% | {formatDate(task.start_date)} → {formatDate(task.end_date)}</p>
                                  <p className="text-xs">EAU Deadline: {formatDate(task.results_to_eau_date)}</p>
                                  {task.drafts?.length > 0 && (
                                    <p className="text-xs mt-1">{task.drafts.length} draft phase{task.drafts.length > 1 ? 's' : ''}</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>

                              {/* EAU marker */}
                              {task.results_to_eau_date && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className="absolute w-3 h-3 rounded-full border-2 bg-white cursor-help z-10"
                                      style={{
                                        borderColor: color.bg,
                                        left: `${((eauLeft - left) / width) * 100}%`,
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)'
                                      }}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-xs font-medium">Results to EAU: {formatDate(task.results_to_eau_date)}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Today legend */}
              <div className="flex items-center gap-4 mt-3 ml-36 text-[11px] text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-px bg-red-400" /> Today
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full border-2 border-gray-400 bg-white" /> EAU Deadline
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-px bg-white/60" /> Draft Phases
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Table */}
        <Card data-testid="ia-calendar-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Due Dates Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ia-calendar-summary-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Subject</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-600">Task</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-600">Weight</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-600">Start</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-600">Due</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-600">EAU</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map(p => {
                    const color = SUBJECT_COLORS[p.subject] || SUBJECT_COLORS.ICT;
                    return p.tasks.map((t, tIdx) => {
                      const dueDate = parseDate(t.final_submission_date || t.end_date);
                      const now = new Date();
                      let status = 'upcoming';
                      let statusLabel = 'Upcoming';
                      let statusColor = 'bg-gray-100 text-gray-600';
                      if (dueDate) {
                        const diff = daysBetween(now, dueDate);
                        if (diff < 0) {
                          status = 'past';
                          statusLabel = 'Past Due';
                          statusColor = 'bg-gray-200 text-gray-500';
                        } else if (diff <= 14) {
                          status = 'soon';
                          statusLabel = `${diff}d left`;
                          statusColor = 'bg-amber-100 text-amber-700';
                        } else if (diff <= 30) {
                          status = 'near';
                          statusLabel = `${diff}d left`;
                          statusColor = 'bg-blue-50 text-blue-600';
                        } else {
                          statusLabel = `${diff}d left`;
                        }
                      }
                      return (
                        <tr
                          key={`${p.id}-${t.id}`}
                          className="border-b border-gray-50 hover:bg-gray-50/80 transition-colors cursor-pointer"
                          onClick={() => openTaskDetail(t, p.subject)}
                          data-testid={`ia-calendar-summary-row-${p.subject.toLowerCase()}-${t.task_number}`}
                        >
                          {tIdx === 0 && (
                            <td className="py-2 px-3 font-semibold" rowSpan={p.tasks.length} style={{ color: color.text }}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color.bg }} />
                                {p.subject}
                              </div>
                            </td>
                          )}
                          <td className="py-2 px-3 text-gray-700">{t.title}</td>
                          <td className="py-2 px-3 text-center font-medium">{t.weighting}%</td>
                          <td className="py-2 px-3 text-center text-gray-500">{formatDate(t.start_date)}</td>
                          <td className="py-2 px-3 text-center font-medium">{formatDate(t.final_submission_date || t.end_date)}</td>
                          <td className="py-2 px-3 text-center text-gray-500">{formatDate(t.results_to_eau_date)}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Task Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="ia-calendar-task-dialog">
            {selectedTask && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: (SUBJECT_COLORS[selectedTask.subject] || SUBJECT_COLORS.ICT).bg }}
                    />
                    {selectedTask.subject} — Task {selectedTask.task_number}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedTask.title}</h3>
                    <Badge variant="outline" className="mt-1">{selectedTask.weighting}% of total</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500 text-xs mb-0.5">Start Date</div>
                      <div className="font-medium">{formatDate(selectedTask.start_date)}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500 text-xs mb-0.5">Due Date</div>
                      <div className="font-medium">{formatDate(selectedTask.final_submission_date || selectedTask.end_date)}</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-blue-500 text-xs mb-0.5">EAU Submission</div>
                      <div className="font-medium text-blue-700">{formatDate(selectedTask.results_to_eau_date)}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500 text-xs mb-0.5">Duration</div>
                      <div className="font-medium">
                        {(() => {
                          const s = parseDate(selectedTask.start_date);
                          const e = parseDate(selectedTask.end_date);
                          if (!s || !e) return '—';
                          const d = daysBetween(s, e);
                          return `${d} day${d !== 1 ? 's' : ''} (${Math.round(d / 7)} weeks)`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {selectedTask.drafts?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Draft Phases</h4>
                      <div className="space-y-2">
                        {selectedTask.drafts.map(d => (
                          <div key={d.draft_number} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5 text-sm">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                              {d.draft_number}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 truncate">{d.title}</div>
                              <div className="text-xs text-gray-500">{formatDate(d.start_date)} → {formatDate(d.end_date)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
